import { defineStore } from "pinia";
import { reactive, ref, type Ref } from "vue";
import { WidgetState, DirectionCongestionWidgetToggle } from "@/features/port-congestion";
import { FilterDefinition, FilterType, FilterTypeEnum } from "@/features/filters";
import { getDeepCopyObject } from "@/global";

export type Filters = object;

export const useFiltersStore = defineStore("filters", () => {
  const filters: Ref<Filters | null> = ref({
    active: {},
    drafts: {},
    archive: {},
    shipments: {},
    import: {},
    dnd: {},
    airshipments: {},
    portCongestion: {},
    airSubscription: {}
  });
  const appliedFilters = ref([]);
  const dndFilterCards = ref([]);
  const congestionWidgetToggle = ref<DirectionCongestionWidgetToggle>({
    IMPORT: {
      dwellTime: false,
      congestionTime: false
    },
    EXPORT: {
      dwellTime: false,
      congestionTime: false
    }
  });
  const dwellTimeData = ref<WidgetState>({
    IMPORT: {
      cardTitle: "",
      cardSubtitle: "",
      showCheckBox: true,
      filterCardContents: []
    },
    EXPORT: {
      cardTitle: "",
      cardSubtitle: "",
      showCheckBox: true,
      filterCardContents: []
    }
  });
  const congestionIncreasedTimeData = ref<WidgetState>({
    IMPORT: {
      cardTitle: "",
      cardSubtitle: "",
      showCheckBox: true,
      filterCardContents: []
    },
    EXPORT: {
      cardTitle: "",
      cardSubtitle: "",
      showCheckBox: true,
      filterCardContents: []
    }
  });

  const setFilters = (value: Filters, category: string = "active"): void => {
    filters.value[category] = value;
  };

  const setAppliedFilters = (value: any[]): void => {
    appliedFilters.value = value;
  };

  const resetAppliedFilters = () => {
    appliedFilters.value = [];
  };

  const setDnDFilterCards = (value: any): void => {
    dndFilterCards.value = value;
  };

  const setCongestionWidgetToggle = (value: any, category: string = "IMPORT"): void => {
    congestionWidgetToggle.value[category] = value;
  };

  const setDwellTimeData = (value: any, category: string = "IMPORT"): void => {
    dwellTimeData.value[category] = value;
  };

  const setCongestionIncreasedTimeData = (value: any, category: string = "IMPORT"): void => {
    congestionIncreasedTimeData.value[category] = value;
  };

  const $reset = () => {
    filters.value = null;
  };

  const filtersApplied = reactive({});
  const filterSearchTerm = ref("");
  const panelSelectedFilters = reactive({});
  const appliedFiltersTags = reactive({});

  const applyFilters = (zone: string, key: string, value: object) => {
    if (!filtersApplied[zone]) filtersApplied[zone] = {};
    filtersApplied[zone][key] = value;
  };

  const bulkApplyFilters = (zone: string, filters: Record<string, any>) => {
    if (!panelSelectedFilters[zone]) panelSelectedFilters[zone] = {};
    if (!filtersApplied[zone]) filtersApplied[zone] = {};
    if (!appliedFiltersTags[zone]) appliedFiltersTags[zone] = {};
    Object.entries(filters).forEach(([key, value]) => {
      panelSelectedFilters[zone][key] = value;
      filtersApplied[zone][key] = value;
      appliedFiltersTags[zone][key] = value;
    });
  };

  const bulkRemoveFilters = (zone: string, filterKeys: Array<string>) => {
    if (!panelSelectedFilters[zone]) return;
    if (!filtersApplied[zone]) return;
    if (!appliedFiltersTags[zone]) return;
    filterKeys.forEach((key) => {
      if (panelSelectedFilters[zone][key]) delete panelSelectedFilters[zone][key];
      if (filtersApplied[zone][key]) delete filtersApplied[zone][key];
      if (appliedFiltersTags[zone][key]) delete appliedFiltersTags[zone][key];
    });
  };

  const applyPanelSelectedFilters = (zone: string, key: string, value: object) => {
    if (!panelSelectedFilters[zone]) panelSelectedFilters[zone] = {};
    panelSelectedFilters[zone][key] = value;
  };

  // Optional scope leaves other zones and pending selections unchanged.
  const setAppliedFiltersTags = (targetZone?: string, onlyKeys?: string[]) => {
    Object.entries(panelSelectedFilters).forEach(([zone, filters]) => {
      if (targetZone && zone !== targetZone) return;
      if (!appliedFiltersTags[zone]) appliedFiltersTags[zone] = {};
      if (!filtersApplied[zone]) filtersApplied[zone] = {};

      Object.entries(filters).forEach(([key, val]: [string, any]) => {
        if (onlyKeys && !onlyKeys.includes(key)) return;
        if (key === "daterange") {
          if (!val?.value?.startDate || !val?.value?.endDate) {
            if (appliedFiltersTags[zone][key]) delete appliedFiltersTags[zone][key];
            if (filtersApplied[zone][key]) delete filtersApplied[zone][key];
            return;
          }
        } else {
          if (!val || (Array.isArray(val.value) && !val.value.length) || !val.value) {
            if (appliedFiltersTags[zone][key]) delete appliedFiltersTags[zone][key];
            if (filtersApplied[zone][key]) delete filtersApplied[zone][key];
            return;
          }
        }

        const copiedValue = getDeepCopyObject(val?.value);

        const finalVal = {
          ...val,
          value: copiedValue
        };

        appliedFiltersTags[zone][key] = finalVal;
        filtersApplied[zone][key] = finalVal;
      });
    });
  };

  const removeAppliedFilterTag = (
    zone: string,
    key: string,
    value?: any,
    extractPayloadFromOption?: (option: any) => any
  ) => {
    const stores = [appliedFiltersTags, filtersApplied, panelSelectedFilters];
    const allStoresHaveArrayValues = stores.every(
      (store) => store[zone] && store[zone][key] && Array.isArray(store[zone][key].value)
    );

    if (value && allStoresHaveArrayValues) {
      stores.forEach((store) => {
        store[zone][key].value = store[zone][key].value.filter((tag: any) => tag.value !== value);

        // If the multi-select array is now empty, drop the entry entirely so it
        // matches setAppliedFiltersTags semantics. Otherwise downstream consumers
        // (e.g. getAppliedFiltersAsPayload) treat `{value: []}` as "still applied".
        if (store[zone][key].value.length === 0) {
          delete store[zone][key];
          return;
        }

        if (store[zone][key].payload && Boolean(extractPayloadFromOption)) {
          store[zone][key].payload = store[zone][key].value.filter(extractPayloadFromOption);
        }
      });
    } else {
      stores.forEach((store) => {
        if (store[zone]) delete store[zone][key];
      });
    }
  };

  const removePanelSelectedFilter = (zone: string, key: string) => {
    if (panelSelectedFilters[zone]) delete panelSelectedFilters[zone][key];
    if (appliedFiltersTags[zone]) delete appliedFiltersTags[zone][key];
    if (filtersApplied[zone]) delete filtersApplied[zone][key];
  };

  const clearAppliedFiltersTags = (zone?: string) => {
    const zonesToClear = zone ? [zone] : Object.keys(appliedFiltersTags);
    zonesToClear.forEach((zoneToClear) => {
      Object.keys(appliedFiltersTags[zoneToClear] || {}).forEach((key) => {
        delete appliedFiltersTags[zoneToClear][key];
        if (filtersApplied[zoneToClear]) delete filtersApplied[zoneToClear][key];
        if (panelSelectedFilters[zoneToClear]) delete panelSelectedFilters[zoneToClear][key];
      });
    });
  };

  const clearAppliedFilters = (filterType?: FilterType, targetZone?: string) => {
    const entries = targetZone
      ? [[targetZone, filtersApplied[targetZone] || {}] as [string, any]]
      : Object.entries(filtersApplied);
    entries.forEach(([zone, filters]) => {
      Object.entries(filters).forEach(([key, value]) => {
        if (!filterType || value?.filterType === filterType) {
          delete filtersApplied[zone][key];
        }
      });
    });
  };

  const clearPanelSelectedFilters = (targetZone?: string) => {
    const panelZonesToClear = targetZone ? [targetZone] : Object.keys(panelSelectedFilters);
    panelZonesToClear.forEach((zone) => {
      Object.keys(panelSelectedFilters[zone] || {}).forEach((key) => {
        delete panelSelectedFilters[zone][key];
      });
    });

    const appliedZonesToClear = targetZone ? [targetZone] : Object.keys(filtersApplied);
    appliedZonesToClear.forEach((zone) => {
      const filters = filtersApplied[zone] || {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value?.filterType === FilterTypeEnum.PANEL_FILTERS) {
          delete filtersApplied[zone][key];
        }
      });
    });
  };

  const clearAllFilters = (targetZone?: string) => {
    const zonesToClear = targetZone ? [targetZone] : Object.keys(filtersApplied);
    zonesToClear.forEach((zone) => {
      delete filtersApplied[zone];
    });
  };

  const setFilterSearchTerm = (term: string) => {
    filterSearchTerm.value = term;
  };

  const getAppliedFiltersAsPayload = (zone: string, usePayloadValue = false, valueAsList = false) => {
    const payloadObj: Record<string, any> = {};
    const payloadList: Array<{ key: string; values: any[] }> = [];
    const allAppliedFilters = filtersApplied[zone] || {};

    Object.keys(allAppliedFilters).forEach((key) => {
      if (allAppliedFilters[key]?.value !== undefined) {
        if (key === "daterange") {
          const dateRangeData = allAppliedFilters[key].dateRange || allAppliedFilters[key].value;
          if (valueAsList) {
            const values = [] as any[];
            if (dateRangeData?.startDate !== undefined) values.push(dateRangeData.startDate);
            if (dateRangeData?.endDate !== undefined) values.push(dateRangeData.endDate);
            payloadList.push({ key: "daterange", values });
          } else {
            payloadObj.startDate = dateRangeData?.startDate;
            payloadObj.endDate = dateRangeData?.endDate;
          }
        } else {
          const filterValue = allAppliedFilters[key].value;

          if (valueAsList) {
            if (usePayloadValue && allAppliedFilters[key]?.payload) {
              const vals = Array.isArray(allAppliedFilters[key].payload)
                ? allAppliedFilters[key].payload
                : [allAppliedFilters[key].payload];
              payloadList.push({ key, values: vals });
            } else if (Array.isArray(filterValue) && filterValue.length > 0) {
              if (filterValue[0]?.label !== undefined && filterValue[0]?.value !== undefined) {
                payloadList.push({ key, values: filterValue.map((item: any) => item.value) });
              } else {
                payloadList.push({ key, values: filterValue });
              }
            } else {
              payloadList.push({ key, values: [filterValue] });
            }
          } else {
            let result;
            if (usePayloadValue && allAppliedFilters[key]?.payload) {
              result = allAppliedFilters[key].payload;
            } else if (Array.isArray(filterValue) && filterValue.length > 0) {
              if (filterValue[0]?.label !== undefined && filterValue[0]?.value !== undefined) {
                result = filterValue.map((item: any) => item.value).join(",");
              } else {
                result = filterValue.join(",");
              }
            } else {
              result = filterValue;
            }
            payloadObj[key] = result;
          }
        }
      }
    });

    return valueAsList ? payloadList : payloadObj;
  };

  /**
   * Gets panel filters (from panelSelectedFilters) and converts them to payload format
   * Similar to getAppliedFiltersAsPayload but reads from panelSelectedFilters instead of filtersApplied
   * @param zone - The zone prefix to get panel filters for
   * @param valueAsList - If true, returns array of {key, values}, otherwise returns object
   * @returns Panel filters in payload format
   */
  const getPanelFiltersAsPayload = (zone: string, valueAsList = false) => {
    const payloadObj: Record<string, any> = {};
    const payloadList: Array<{ key: string; values: any[] }> = [];
    const allPanelFilters = panelSelectedFilters[zone] || {};

    Object.keys(allPanelFilters).forEach((key) => {
      // Skip internal metadata
      if (key === "__payload") return;

      const filterData = allPanelFilters[key];
      if (filterData?.value !== undefined) {
        if (key === "daterange") {
          const dateRangeData = filterData.dateRange || filterData.value;
          if (valueAsList) {
            const values = [] as any[];
            if (dateRangeData?.startDate !== undefined) values.push(dateRangeData.startDate);
            if (dateRangeData?.endDate !== undefined) values.push(dateRangeData.endDate);
            payloadList.push({ key: "daterange", values });
          } else {
            payloadObj.startDate = dateRangeData?.startDate;
            payloadObj.endDate = dateRangeData?.endDate;
          }
        } else {
          const filterValue = filterData.value;

          if (valueAsList) {
            // Extract values - handle both array and single values
            const filterValues = Array.isArray(filterValue) ? filterValue : [filterValue];
            // If values are objects with {label, value, mcOption, visible}, extract just the value property
            const values = filterValues.map((item: any) =>
              typeof item === "object" && item !== null && "value" in item ? item.value : item
            );
            payloadList.push({ key, values });
          } else {
            // For object format, join values as comma-separated string
            let result;
            if (Array.isArray(filterValue) && filterValue.length > 0) {
              if (filterValue[0]?.label !== undefined && filterValue[0]?.value !== undefined) {
                result = filterValue.map((item: any) => item.value).join(",");
              } else {
                result = filterValue.join(",");
              }
            } else {
              result = filterValue;
            }
            payloadObj[key] = result;
          }
        }
      }
    });

    // Filter out entries with empty values arrays to prevent API errors
    const filteredPayloadList = payloadList.filter((item) => item.values && item.values.length > 0);
    return valueAsList ? filteredPayloadList : payloadObj;
  };

  const resetFilterToDefaultRecipe = (recipe: Array<FilterDefinition>, filterType: FilterType, zonePrefix?: string) => {
    recipe.forEach((filter: FilterDefinition) => {
      const defaultValue = filter.value;
      if (defaultValue === undefined || defaultValue === null) return;

      const { key: filterKey, label: filterLabel, sectionLabel, groupId, groupLabel } = filter;

      const fullKey = zonePrefix ? `${zonePrefix}.${filterKey}` : filterKey;
      const [zone, key] = fullKey.includes(".") ? fullKey.split(".") : ["default", fullKey];

      const zonePanel = panelSelectedFilters[zone] || {};
      const zoneApplied = filtersApplied[zone] || {};

      if (filterType === FilterTypeEnum.PANEL_FILTERS && zonePanel[key]) return;
      if (filterType !== FilterTypeEnum.PANEL_FILTERS && zoneApplied[key]) return;

      const filterData = {
        ...defaultValue,
        ...(groupId ? { groupId } : {}),
        ...(groupLabel ? { groupLabel } : {}),
        ...(sectionLabel ? { sectionLabel } : {}),
        ...(filterLabel ? { filterLabel } : {}),
        filterType,
        filterKey
      };

      if (filterType === FilterTypeEnum.PANEL_FILTERS) {
        applyPanelSelectedFilters(zone, key, filterData);
        applyFilters(zone, key, filterData);
      } else {
        applyFilters(zone, key, filterData);
      }
    });
  };

  return {
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    dndFilterCards,
    setDnDFilterCards,
    congestionWidgetToggle,
    setCongestionWidgetToggle,
    dwellTimeData,
    setDwellTimeData,
    congestionIncreasedTimeData,
    setCongestionIncreasedTimeData,
    $reset,
    resetAppliedFilters,
    panelSelectedFilters,
    applyPanelSelectedFilters,
    removePanelSelectedFilter,
    clearPanelSelectedFilters,
    filtersApplied,
    applyFilters,
    bulkApplyFilters,
    bulkRemoveFilters,
    clearAppliedFilters,
    clearAllFilters,
    filterSearchTerm,
    setFilterSearchTerm,
    appliedFiltersTags,
    setAppliedFiltersTags,
    removeAppliedFilterTag,
    clearAppliedFiltersTags,
    getAppliedFiltersAsPayload,
    getPanelFiltersAsPayload,
    resetFilterToDefaultRecipe
  };
});
